import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';

type Props = {
  children: ReactNode;
};

export type SalesOrdersDashboardSearchCondition = {
  identifier: string;
  team_ids: number[];
  user_ids: number[];
  product_ids: number[];
  sales_context_ids: number[];
  business_partner_ids: number[];
  agency_ids: number[];
  fiscal_year: number;
};
export const defaultSalesOrdersDashboardSearchCondition: SalesOrdersDashboardSearchCondition = {
  identifier: '',
  team_ids: [],
  user_ids: [],
  product_ids: [],
  sales_context_ids: [],
  business_partner_ids: [],
  agency_ids: [],
  fiscal_year: 0,
};

export type PurchaseOrdersDashboardSearchCondition = {
  identifier: string;
  team_ids: number[];
  user_ids: number[];
  payment_context_ids: number[];
  fiscal_year: number;
};
export const defaultPurchaseOrdersDashboardSearchCondition: PurchaseOrdersDashboardSearchCondition = {
  identifier: '',
  team_ids: [],
  user_ids: [],
  payment_context_ids: [],
  fiscal_year: 0,
};

export type WeeklyComparisonDashboardSearchCondition = {
  identifier: string;
  before_weekly_summary_id: number;
  after_weekly_summary_id: number;
  team_ids: number[];
  user_ids: number[];
  product_ids: number[];
  business_partner_ids: number[];
  agency_ids: number[];
};
export const defaultWeeklyComparisonDashboardSearchCondition: WeeklyComparisonDashboardSearchCondition = {
  identifier: '',
  before_weekly_summary_id: 0,
  after_weekly_summary_id: 0,
  team_ids: [],
  user_ids: [],
  product_ids: [],
  business_partner_ids: [],
  agency_ids: [],
};

export type WeeklyComparisonCollapsedCondition = {
  attackListCollapsed: boolean;
  initialProposalCollapsed: boolean;
  detailProposalCollapsed: boolean;
  underNegotiationCollapsed: boolean;
  contractingCollapsed: boolean;
  completedCollapsed: boolean;
  pendingCollapsed: boolean;
  lostOrderCollapsed: boolean;
};
export const defaultWeeklyComparisonCollapsedCondition: WeeklyComparisonCollapsedCondition = {
  attackListCollapsed: false,
  initialProposalCollapsed: false,
  detailProposalCollapsed: false,
  underNegotiationCollapsed: false,
  contractingCollapsed: false,
  completedCollapsed: false,
  pendingCollapsed: false,
  lostOrderCollapsed: false,
};

export type PlDashboardSearchCondition = {
  identifier: string;
  team_ids: number[];
  user_ids: number[];
  fiscal_year: number;
  use_external_expense: boolean;
};
export const defaultPlDashboardSearchCondition: PlDashboardSearchCondition = {
  identifier: '',
  team_ids: [],
  user_ids: [],
  fiscal_year: 0,
  use_external_expense: false,
};

export type DashboardSearchConditionType = {
  salesOrdersDashboardSearchCondition: SalesOrdersDashboardSearchCondition;
  setSalesOrdersDashboardSearchCondition: Dispatch<SetStateAction<SalesOrdersDashboardSearchCondition>>;
  purchaseOrdersDashboardSearchCondition: PurchaseOrdersDashboardSearchCondition;
  setPurchaseOrdersDashboardSearchCondition: Dispatch<SetStateAction<PurchaseOrdersDashboardSearchCondition>>;
  weeklyComparisonDashboardSearchCondition: WeeklyComparisonDashboardSearchCondition;
  setWeeklyComparisonDashboardSearchCondition: Dispatch<SetStateAction<WeeklyComparisonDashboardSearchCondition>>;
  weeklyComparisonCollapsedCondition: WeeklyComparisonCollapsedCondition;
  setWeeklyComparisonCollapsedCondition: Dispatch<SetStateAction<WeeklyComparisonCollapsedCondition>>;
  plDashboardSearchCondition: PlDashboardSearchCondition;
  setPlDashboardSearchCondition: Dispatch<SetStateAction<PlDashboardSearchCondition>>;
};

export const DashboardSearchConditionContext = createContext<DashboardSearchConditionType>({
  salesOrdersDashboardSearchCondition: defaultSalesOrdersDashboardSearchCondition,
  setSalesOrdersDashboardSearchCondition: () => {},
  purchaseOrdersDashboardSearchCondition: defaultPurchaseOrdersDashboardSearchCondition,
  setPurchaseOrdersDashboardSearchCondition: () => {},
  weeklyComparisonDashboardSearchCondition: defaultWeeklyComparisonDashboardSearchCondition,
  setWeeklyComparisonDashboardSearchCondition: () => {},
  weeklyComparisonCollapsedCondition: defaultWeeklyComparisonCollapsedCondition,
  setWeeklyComparisonCollapsedCondition: () => {},
  plDashboardSearchCondition: defaultPlDashboardSearchCondition,
  setPlDashboardSearchCondition: () => {},
});

export const DashboardSearchConditionProvider = ({ children }: Props) => {
  const { _w_ } = useCurrentWorkspace();

  const [salesOrdersDashboardSearchCondition, setSalesOrdersDashboardSearchCondition] =
    useState<SalesOrdersDashboardSearchCondition>(defaultSalesOrdersDashboardSearchCondition);
  const [purchaseOrdersDashboardSearchCondition, setPurchaseOrdersDashboardSearchCondition] =
    useState<PurchaseOrdersDashboardSearchCondition>(defaultPurchaseOrdersDashboardSearchCondition);
  const [weeklyComparisonDashboardSearchCondition, setWeeklyComparisonDashboardSearchCondition] =
    useState<WeeklyComparisonDashboardSearchCondition>(defaultWeeklyComparisonDashboardSearchCondition);
  const [weeklyComparisonCollapsedCondition, setWeeklyComparisonCollapsedCondition] =
    useState<WeeklyComparisonCollapsedCondition>(defaultWeeklyComparisonCollapsedCondition);
  const [plDashboardSearchCondition, setPlDashboardSearchCondition] = useState<PlDashboardSearchCondition>(
    defaultPlDashboardSearchCondition
  );

  useEffect(() => {
    if (_w_) setPurchaseOrdersDashboardSearchCondition((c) => ({ ...c, identifier: _w_!.identifier }));
  }, [_w_]);

  return (
    <DashboardSearchConditionContext.Provider
      value={{
        salesOrdersDashboardSearchCondition,
        setSalesOrdersDashboardSearchCondition,
        purchaseOrdersDashboardSearchCondition,
        setPurchaseOrdersDashboardSearchCondition,
        weeklyComparisonDashboardSearchCondition,
        setWeeklyComparisonDashboardSearchCondition,
        weeklyComparisonCollapsedCondition,
        setWeeklyComparisonCollapsedCondition,
        plDashboardSearchCondition,
        setPlDashboardSearchCondition,
      }}
    >
      {children}
    </DashboardSearchConditionContext.Provider>
  );
};

export const useDashboardSearchCondition = () => {
  return useContext(DashboardSearchConditionContext);
};
